// src/layout/NavBar.tsx — Editorial / Swiss redesign
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ROUTE_NAMES } from "../routes/routeConstants";
import { useAuth } from "../provider/AuthProvider";
import { useLoginModal } from "../context/LoginModalContext";
import { useState, useEffect, useCallback } from "react";
import {
  getNotifications,
  getUnreadCount,
  markAsRead,
  markAllAsRead,
  deleteNotification,
  Notification,
} from "../services/notificationService";
import { getAuthHeaders } from "../utils/authHeaders";
import { AcaIcon } from "../components/shared/aca";
import { VELLUM as T, SANS, MONO } from "../components/shared/folio/kit";
import { API_URL } from "../config/api";

/* The folio vocabulary mapped onto the token names this file already used.
   Drawn as a sheet header: vellum ground, a heavy ink rule under it, and the
   teal accent. Only the palette changes here - every link, the notification
   poll, the auth states and the mobile menu are exactly as they were. */
const pal = {
  paper: T.ground,
  ink: T.ink,
  inkSoft: T.mute,
  inkMute: T.mute,
  rule: T.rule,
  ruleSoft: T.rule,
  accent: T.accent,
  /* Type set on the accent. Not pure white: the sheet's own light tone,
     so the header carries no colour the palette has not declared. */
  onAccent: T.onAccent,
};

const fam = {
  sans: SANS,
  mono: MONO,
  /* The folio has no serif; its quiet voice is the sans at a smaller size. */
  serif: SANS,
};

const navLinks: { id: string; label: string; href: string }[] = [
  { id: "jobs",    label: "01 · Browse Jobs", href: ROUTE_NAMES.PAGES.JOBS },
  { id: "about",   label: "02 · About",       href: ROUTE_NAMES.PAGES.ABOUT },
  { id: "blog",    label: "03 · Blog",        href: ROUTE_NAMES.PAGES.BLOG },
  { id: "contact", label: "04 · Contact",     href: ROUTE_NAMES.PAGES.CONTACT },
];

function activeIdFromPath(pathname: string): string {
  if (pathname === "/" || pathname === ROUTE_NAMES.PAGES.HOME) return "index";
  if (pathname.startsWith(ROUTE_NAMES.PAGES.JOBS)) return "jobs";
  if (pathname.startsWith(ROUTE_NAMES.PAGES.ABOUT)) return "about";
  if (pathname.startsWith(ROUTE_NAMES.PAGES.BLOG)) return "blog";
  if (pathname.startsWith(ROUTE_NAMES.PAGES.CONTACT)) return "contact";
  return "";
}

export const NavBar: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { openLoginModal, openRegisterModal } = useLoginModal();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [isWiping, setIsWiping] = useState(false);

  const active = activeIdFromPath(location.pathname);

  const handleWipeDatabase = async () => {
    if (!window.confirm("Are you sure you want to wipe the entire staging database? This cannot be undone.")) return;
    if (!window.confirm("FINAL WARNING: All users, jobs, applications, and messages will be permanently deleted. Continue?")) return;
    setIsWiping(true);
    try {
      const response = await fetch(`${API_URL}/admin/reset-database`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        credentials: "include",
        body: JSON.stringify({ confirm: "WIPE_ALL_DATA" }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "Wipe failed");
      alert(`Database wiped successfully!\n\n${Object.entries(data.deleted).map(([k, v]) => `${k}: ${v} deleted`).join("\n")}`);
      logout();
      navigate(ROUTE_NAMES.PAGES.HOME);
    } catch (err) {
      alert(`Database wipe failed: ${(err as Error).message}`);
    } finally {
      setIsWiping(false);
    }
  };

  const fetchNotifications = useCallback(async () => {
    if (!user) return;
    try {
      const [notifResponse, count] = await Promise.all([getNotifications(1, 10), getUnreadCount()]);
      setNotifications(notifResponse.notifications);
      setUnreadCount(count);
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
    }
  }, [user]);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const handleMarkAsRead = async (id: string) => {
    try {
      await markAsRead(id);
      setNotifications((prev) => prev.map((n) => (n._id === id ? { ...n, read: true } : n)));
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (error) {
      console.error("Failed to mark notification as read:", error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await markAllAsRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error("Failed to mark all as read:", error);
    }
  };

  const handleDeleteNotification = async (id: string) => {
    try {
      await deleteNotification(id);
      const deleted = notifications.find((n) => n._id === id);
      setNotifications((prev) => prev.filter((n) => n._id !== id));
      if (deleted && !deleted.read) setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (error) {
      console.error("Failed to delete notification:", error);
    }
  };

  const mapLegacyUrl = (url: string): string => {
    const legacyUrlMap: Record<string, string> = {
      "/my-applications": "/job-seeker-dashboard?tab=applications",
      "/employer/applications": "/employer-dashboard?tab=applications",
    };
    return legacyUrlMap[url] || url;
  };

  const handleNotificationClick = (notification: Notification) => {
    handleMarkAsRead(notification._id);
    if (notification.actionUrl) {
      navigate(mapLegacyUrl(notification.actionUrl));
      setIsNotificationsOpen(false);
    }
  };

  const handleLogout = () => {
    logout();
    setIsMobileMenuOpen(false);
    navigate(ROUTE_NAMES.PAGES.HOME);
  };

  const homeHref = user
    ? user.role === "employee"
      ? ROUTE_NAMES.PAGES.JOBS
      : ROUTE_NAMES.PAGES.EMPLOYER_DASHBOARD
    : ROUTE_NAMES.PAGES.HOME;

  return (
    <header
      style={{
        /* A drawing sheet's head is a heavy rule, not a hairline. */
        borderBottom: `1.5px solid ${pal.ink}`,
        background: pal.paper,
        position: "sticky",
        top: 0,
        zIndex: 50,
        fontFamily: fam.sans,
      }}
    >
      <div
        style={{
          maxWidth: 1280,
          margin: "0 auto",
          padding: "18px 20px",
          display: "grid",
          gridTemplateColumns: "auto 1fr auto",
          gap: 32,
          alignItems: "center",
        }}
      >
        {/* Brand */}
        <Link
          to={homeHref}
          style={{ display: "flex", alignItems: "baseline", gap: 10, textDecoration: "none", color: pal.ink }}
        >
          {/* The folio's mark: an outlined square, drawn rather than filled. */}
          <div aria-hidden style={{ width: 12, height: 12, border: `1.5px solid ${pal.accent}`, alignSelf: "center" }} />
          <div style={{ fontFamily: fam.sans, fontSize: 20, fontWeight: 700, letterSpacing: "-0.02em" }}>Acanetic</div>
          <div
            className="hidden sm:block"
            style={{
              fontFamily: fam.mono,
              fontSize: 10.5,
              color: pal.inkSoft,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
            }}
          >
            / {active || "index"}
          </div>
        </Link>

        {/* Center nav */}
        <nav
          className="hidden lg:flex"
          style={{
            gap: 24,
            fontFamily: fam.mono,
            fontSize: 11,
            letterSpacing: "0.16em",
            textTransform: "uppercase",
            justifyContent: "center",
          }}
        >
          {navLinks.map((l) => (
            <Link
              key={l.id}
              to={l.href}
              onClick={(e) => {
                if (!user && l.id === "jobs") {
                  e.preventDefault();
                  openLoginModal();
                }
              }}
              style={{
                color: active === l.id ? pal.ink : pal.inkSoft,
                textDecoration: "none",
                borderBottom: active === l.id ? `2px solid ${pal.accent}` : "2px solid transparent",
                paddingBottom: 4,
              }}
            >
              {l.label}
            </Link>
          ))}
        </nav>

        {/* Right rail */}
        <div
          className="hidden sm:flex"
          style={{
            gap: 18,
            alignItems: "center",
            fontFamily: fam.mono,
            fontSize: 11,
            letterSpacing: "0.16em",
            textTransform: "uppercase",
          }}
        >
          {user && user.role === "employer" && (
            <Link
              to={ROUTE_NAMES.PAGES.POST_JOB}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 10,
                padding: "11px 16px",
                background: pal.accent,
                color: pal.onAccent,
                textDecoration: "none",
              }}
            >
              <span>Post a job</span>
              <span style={{ width: 16, height: 1, background: pal.onAccent }} />
              <span style={{ fontSize: 14 }}>→</span>
            </Link>
          )}

          {!user ? (
            <>
              <button
                onClick={openLoginModal}
                style={{
                  color: pal.ink,
                  cursor: "pointer",
                  background: "transparent",
                  border: "none",
                  padding: 0,
                  fontFamily: "inherit",
                  fontSize: "inherit",
                  letterSpacing: "inherit",
                  textTransform: "inherit",
                }}
              >
                Sign in
              </button>
              <button
                onClick={openRegisterModal}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "11px 16px",
                  background: pal.ink,
                  color: pal.paper,
                  cursor: "pointer",
                  border: "none",
                  fontFamily: "inherit",
                  fontSize: "inherit",
                  letterSpacing: "inherit",
                  textTransform: "inherit",
                }}
              >
                <span>Join now</span>
                <span style={{ width: 16, height: 1, background: pal.paper }} />
                <span style={{ fontSize: 14 }}>→</span>
              </button>
            </>
          ) : (
            <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
              {/* Notification bell */}
              <div style={{ position: "relative" }}>
                <button
                  onClick={() => setIsNotificationsOpen(!isNotificationsOpen)}
                  style={{
                    position: "relative",
                    background: "transparent",
                    border: "none",
                    color: pal.ink,
                    cursor: "pointer",
                    padding: 4,
                    display: "flex",
                    alignItems: "center",
                  }}
                  aria-label="Notifications"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M15 17h5l-1.4-1.4A2 2 0 0 1 18 14.16V11a6 6 0 0 0-4-5.66V5a2 2 0 1 0-4 0v.34C7.67 6.16 6 8.4 6 11v3.16c0 .54-.21 1.06-.6 1.44L4 17h5m6 0v1a3 3 0 1 1-6 0v-1m6 0H9" />
                  </svg>
                  {unreadCount > 0 && (
                    <span
                      style={{
                        position: "absolute",
                        top: -4,
                        right: -6,
                        minWidth: 16,
                        height: 16,
                        padding: "0 4px",
                        borderRadius: 8,
                        background: pal.accent,
                        color: pal.onAccent,
                        fontFamily: fam.mono,
                        fontSize: 9,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        letterSpacing: 0,
                      }}
                    >
                      {unreadCount > 9 ? "9+" : unreadCount}
                    </span>
                  )}
                </button>

                {isNotificationsOpen && (
                  <>
                    <div style={{ position: "fixed", inset: 0, zIndex: 40 }} onClick={() => setIsNotificationsOpen(false)} />
                    <div
                      style={{
                        position: "absolute",
                        right: 0,
                        top: "calc(100% + 8px)",
                        width: 360,
                        background: pal.paper,
                        border: `1px solid ${pal.rule}`,
                        zIndex: 50,
                        boxShadow: "0 20px 60px rgba(20,18,12,0.18)",
                      }}
                    >
                      <div style={{ padding: 16, borderBottom: `1px solid ${pal.rule}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <div style={{ fontFamily: fam.mono, fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase", color: pal.ink }}>Notifications</div>
                        {unreadCount > 0 && (
                          <button
                            onClick={handleMarkAllAsRead}
                            style={{ background: "transparent", border: "none", color: pal.accent, cursor: "pointer", fontFamily: fam.mono, fontSize: 10, letterSpacing: "0.16em", textTransform: "uppercase" }}
                          >
                            Mark all read
                          </button>
                        )}
                      </div>
                      <div style={{ maxHeight: 360, overflowY: "auto" }}>
                        {notifications.length === 0 ? (
                          <div style={{ padding: 28, textAlign: "center", color: pal.inkSoft, fontFamily: fam.serif, fontSize: 14, fontStyle: "italic" }}>
                            No notifications yet.
                          </div>
                        ) : (
                          notifications.map((notification) => (
                            <div
                              key={notification._id}
                              onClick={() => handleNotificationClick(notification)}
                              style={{
                                padding: 14,
                                borderBottom: `1px solid ${pal.ruleSoft}`,
                                background: notification.read ? "transparent" : "rgba(194,74,30,0.06)",
                                cursor: "pointer",
                              }}
                            >
                              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12 }}>
                                <div style={{ fontFamily: fam.sans, fontSize: 13.5, color: pal.ink, fontWeight: 600 }}>{notification.title}</div>
                                {!notification.read && <span style={{ width: 6, height: 6, borderRadius: 999, background: pal.accent, flexShrink: 0 }} />}
                              </div>
                              <div style={{ fontFamily: fam.sans, fontSize: 12.5, color: pal.inkSoft, marginTop: 4, lineHeight: 1.4 }}>{notification.message}</div>
                              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 8, fontFamily: fam.mono, fontSize: 10, color: pal.inkMute, letterSpacing: "0.08em" }}>
                                <span>{new Date(notification.createdAt).toLocaleDateString()}</span>
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleDeleteNotification(notification._id); }}
                                  style={{ background: "transparent", border: "none", color: pal.inkMute, cursor: "pointer", fontFamily: "inherit", fontSize: "inherit", letterSpacing: "inherit" }}
                                >
                                  Delete
                                </button>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>

              <Link
                to={user.role === "employer" ? ROUTE_NAMES.PAGES.EMPLOYER_DASHBOARD : ROUTE_NAMES.PAGES.JOB_SEEKER_DASHBOARD}
                style={{ color: pal.ink, textDecoration: "none" }}
              >
                Dashboard
              </Link>

              {user.email === "georgievmarty@gmail.com" && (
                <button
                  onClick={handleWipeDatabase}
                  disabled={isWiping}
                  style={{
                    background: "transparent",
                    border: "none",
                    color: pal.accent,
                    cursor: isWiping ? "not-allowed" : "pointer",
                    fontFamily: "inherit",
                    fontSize: "inherit",
                    letterSpacing: "inherit",
                    textTransform: "inherit",
                  }}
                >
                  {isWiping ? "Wiping…" : "Wipe DB"}
                </button>
              )}

              <button
                onClick={handleLogout}
                style={{
                  background: "transparent",
                  border: "none",
                  color: pal.ink,
                  cursor: "pointer",
                  fontFamily: "inherit",
                  fontSize: "inherit",
                  letterSpacing: "inherit",
                  textTransform: "inherit",
                }}
              >
                Logout
              </button>
            </div>
          )}
        </div>

        {/* Mobile menu trigger */}
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="sm:hidden"
          style={{
            background: "transparent",
            border: `1px solid ${pal.ink}`,
            padding: 8,
            cursor: "pointer",
            color: pal.ink,
          }}
          aria-label="Menu"
        >
          <AcaIcon name={isMobileMenuOpen ? "close" : "menu"} size={18} />
        </button>
      </div>

      {/* Mobile menu */}
      {isMobileMenuOpen && (
        <div
          className="sm:hidden"
          style={{
            borderTop: `1px solid ${pal.rule}`,
            background: pal.paper,
            padding: "16px 20px",
          }}
        >
          <div style={{ display: "grid", gap: 4, fontFamily: fam.mono, fontSize: 12, letterSpacing: "0.16em", textTransform: "uppercase" }}>
            {navLinks.map((l) => (
              <Link
                key={l.id}
                to={l.href}
                onClick={(e) => {
                  setIsMobileMenuOpen(false);
                  if (!user && l.id === "jobs") {
                    e.preventDefault();
                    openLoginModal();
                  }
                }}
                style={{ padding: "12px 0", color: pal.ink, textDecoration: "none", borderBottom: `1px solid ${pal.ruleSoft}` }}
              >
                {l.label}
              </Link>
            ))}
          </div>
          <div style={{ marginTop: 16, display: "grid", gap: 8 }}>
            {!user ? (
              <>
                <button
                  onClick={() => { setIsMobileMenuOpen(false); openLoginModal(); }}
                  style={{
                    width: "100%",
                    padding: "12px 16px",
                    background: "transparent",
                    border: `1px solid ${pal.ink}`,
                    color: pal.ink,
                    cursor: "pointer",
                    fontFamily: fam.mono,
                    fontSize: 11,
                    letterSpacing: "0.18em",
                    textTransform: "uppercase",
                  }}
                >
                  Sign in
                </button>
                <button
                  onClick={() => { setIsMobileMenuOpen(false); openRegisterModal(); }}
                  style={{
                    width: "100%",
                    padding: "12px 16px",
                    background: pal.ink,
                    color: pal.paper,
                    border: "none",
                    cursor: "pointer",
                    fontFamily: fam.mono,
                    fontSize: 11,
                    letterSpacing: "0.18em",
                    textTransform: "uppercase",
                  }}
                >
                  Join now →
                </button>
              </>
            ) : (
              <>
                <Link
                  to={user.role === "employer" ? ROUTE_NAMES.PAGES.EMPLOYER_DASHBOARD : ROUTE_NAMES.PAGES.JOB_SEEKER_DASHBOARD}
                  onClick={() => setIsMobileMenuOpen(false)}
                  style={{ padding: "12px 16px", border: `1px solid ${pal.ink}`, color: pal.ink, textAlign: "center", fontFamily: fam.mono, fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase", textDecoration: "none" }}
                >
                  Dashboard
                </Link>
                {user.role === "employer" && (
                  <Link
                    to={ROUTE_NAMES.PAGES.POST_JOB}
                    onClick={() => setIsMobileMenuOpen(false)}
                    style={{ padding: "12px 16px", background: pal.accent, color: pal.onAccent, textAlign: "center", fontFamily: fam.mono, fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase", textDecoration: "none" }}
                  >
                    Post a job →
                  </Link>
                )}
                {user.email === "georgievmarty@gmail.com" && (
                  <button
                    onClick={() => { setIsMobileMenuOpen(false); handleWipeDatabase(); }}
                    disabled={isWiping}
                    style={{
                      padding: "12px 16px",
                      background: "transparent",
                      border: `1px solid ${pal.accent}`,
                      color: pal.accent,
                      cursor: isWiping ? "not-allowed" : "pointer",
                      fontFamily: fam.mono,
                      fontSize: 11,
                      letterSpacing: "0.18em",
                      textTransform: "uppercase",
                    }}
                  >
                    {isWiping ? "Wiping…" : "Wipe DB"}
                  </button>
                )}
                <button
                  onClick={handleLogout}
                  style={{
                    padding: "12px 16px",
                    background: "transparent",
                    border: `1px solid ${pal.inkSoft}`,
                    color: pal.inkSoft,
                    cursor: "pointer",
                    fontFamily: fam.mono,
                    fontSize: 11,
                    letterSpacing: "0.18em",
                    textTransform: "uppercase",
                  }}
                >
                  Logout
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
};
